// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { consoleService } from '../../services/consoleService';
import { Match, MatchSearchFilters } from '../../types/matches';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import './Matches.scss';

const Matches: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const userRole = authService.getSessionRole();
  const canViewMatches = userRole <= UserRole.USER_ROLE_MODIFIER;

  // Get filter values from URL params
  const node = searchParams.get('node') || '';
  const authoritative = searchParams.get('authoritative');
  const label = searchParams.get('label') || '';
  const minSize = searchParams.get('min_size') || '';
  const maxSize = searchParams.get('max_size') || '';

  useEffect(() => {
    if (canViewMatches) {
      loadMatches();
      // Set up auto-refresh every 5 seconds
      const interval = setInterval(loadMatches, 5000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [searchParams, canViewMatches]);

  const loadMatches = async (isRefresh = false) => {
    try {
      setError('');
      if (isRefresh) {
        setRefreshing(true);
      }

      const filters: MatchSearchFilters = {
        node: node || undefined,
        authoritative: authoritative === 'true' ? true : authoritative === 'false' ? false : undefined,
        label: label || undefined,
        min_size: minSize ? parseInt(minSize) : undefined,
        max_size: maxSize ? parseInt(maxSize) : undefined,
      };

      const data = await consoleService.listMatches(filters);
      setMatches(data.matches || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load matches');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleFilterChange = (newFilters: Partial<MatchSearchFilters>) => {
    const params = new URLSearchParams();
    
    if (newFilters.node) params.set('node', newFilters.node);
    if (newFilters.authoritative !== undefined) params.set('authoritative', newFilters.authoritative.toString());
    if (newFilters.label) params.set('label', newFilters.label);
    if (newFilters.min_size !== undefined) params.set('min_size', newFilters.min_size.toString());
    if (newFilters.max_size !== undefined) params.set('max_size', newFilters.max_size.toString());
    
    setSearchParams(params);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getAuthoritativeBadge = (authoritative: boolean) => {
    return (
      <span className={`badge ${authoritative ? 'bg-success' : 'bg-info'}`}>
        {authoritative ? 'Authoritative' : 'Relayed'}
      </span>
    );
  };

  if (!canViewMatches) {
    return (
      <div className="matches-container">
        <h2 className="pb-4">Running Matches</h2>
        <div className="alert alert-warning">
          <strong>Access Denied:</strong> You don't have permission to view running matches.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="matches-container">
        <h2 className="pb-4">Running Matches</h2>
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading matches...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="matches-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          Running Matches
          <span className="badge bg-primary ms-2">{matches.length}</span>
        </h2>
        <div className="d-flex align-items-center">
          <small className="text-muted me-3">
            <i className="fas fa-sync-alt me-1"></i>
            Auto-refreshing every 5s
          </small>
          <button
            className="btn btn-outline-primary"
            onClick={() => loadMatches(true)}
            disabled={refreshing}
            title="Refresh matches"
          >
            {refreshing ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Refreshing...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt me-2"></i>
                Refresh Now
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Node</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by node..."
                value={node}
                onChange={(e) => handleFilterChange({ node: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={authoritative || ''}
                onChange={(e) => handleFilterChange({ authoritative: e.target.value === '' ? undefined : e.target.value === 'true' })}
              >
                <option value="">All Types</option>
                <option value="true">Authoritative</option>
                <option value="false">Relayed</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Label</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by label..."
                value={label}
                onChange={(e) => handleFilterChange({ label: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Min Size</label>
              <input
                type="number"
                className="form-control"
                placeholder="Min"
                value={minSize}
                onChange={(e) => handleFilterChange({ min_size: e.target.value ? parseInt(e.target.value) : undefined })}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Max Size</label>
              <input
                type="number"
                className="form-control"
                placeholder="Max"
                value={maxSize}
                onChange={(e) => handleFilterChange({ max_size: e.target.value ? parseInt(e.target.value) : undefined })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Matches Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Match ID</th>
                  <th>Type</th>
                  <th>Label</th>
                  <th>Handler</th>
                  <th>Size</th>
                  <th>Tick Rate</th>
                  <th>Node</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((match) => (
                  <tr key={match.match_id}>
                    <td>
                      <code className="match-id" title={match.match_id}>
                        {truncateText(match.match_id, 20)}
                      </code>
                    </td>
                    <td>
                      {getAuthoritativeBadge(match.authoritative)}
                    </td>
                    <td>
                      {match.label ? (
                        <span className="match-label">{match.label}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <code className="handler-name">{match.handler_name}</code>
                    </td>
                    <td>
                      <span className="badge bg-secondary">{match.size}</span>
                    </td>
                    <td>
                      <span className="text-muted">{match.tick_rate} Hz</span>
                    </td>
                    <td>
                      <span className="badge bg-outline-primary">{match.node}</span>
                    </td>
                    <td>{formatDate(match.create_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {matches.length === 0 && (
            <div className="text-center py-4">
              <div className="mb-3">
                <i className="fas fa-gamepad fa-3x text-muted"></i>
              </div>
              <p className="text-muted">No running matches found.</p>
              <small className="text-muted">
                Matches will appear here when players are actively playing multiplayer games.
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Match Statistics */}
      {matches.length > 0 && (
        <div className="row mt-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-primary">{matches.length}</h5>
                <p className="card-text">Total Matches</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-success">
                  {matches.filter(m => m.authoritative).length}
                </h5>
                <p className="card-text">Authoritative</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-info">
                  {matches.filter(m => !m.authoritative).length}
                </h5>
                <p className="card-text">Relayed</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-warning">
                  {matches.reduce((sum, m) => sum + m.size, 0)}
                </h5>
                <p className="card-text">Total Players</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Matches;
